import type { VoidFunctionComponent } from 'react';

import { AllRevisionSources } from '../../../../../typings/enums';
import MenuSelectItem from '../../../generic/Menu/MenuSelectItem';
import type { VCSHost } from '../../../../../shared/projectsV2/vcs';
import type { RevisionSource } from '../../../../../typings/enums';

import { getSourceText } from './utils';
import type { RevisionDropdownProps } from './types';

const allSourcesOption = { label: 'All sources', value: '' };

const allOptions = AllRevisionSources.map((source) => ({
  value: source,
  label: getSourceText(source) || '',
}));

/**
 * Get the options for the dropdown based on the VCS host.
 * Always includes the CLI option to allow users to filter by CLI to accommodate hybrid projects (CLI + VCS).
 * If the VCS host is not recognized, all options are returned.
 */
const getOptions = (vcsHost: VCSHost) => {
  const cliValue = 'cli';
  switch (vcsHost) {
    case 'gitlab':
      return allOptions.filter((option) => ['gitlab', cliValue].includes(option.value));
    case 'azure_repos':
      return allOptions.filter((option) => ['azure', cliValue].includes(option.value));
    case 'github':
    case 'github-app':
      return allOptions.filter((option) => ['github', cliValue].includes(option.value));
    case 'bitbucket_cloud':
    case 'bitbucket_server':
      return allOptions.filter((option) => ['bitbucket', cliValue].includes(option.value));
    default:
      return allOptions;
  }
};

/**
 * SourceDropdown Component
 *
 * A dropdown for selecting a revision source, with "All sources" as the default option.
 * Integrates with the MenuSelectItem for consistent styling and functionality.
 */
const SourceDropdown: VoidFunctionComponent<{
  project: RevisionDropdownProps['project'];
  source: RevisionSource | null;
  onClick: (source: RevisionSource | null) => void;
}> = ({ source, onClick, project }) => {
  const options = [allSourcesOption, ...getOptions(project?.data?.vcs_host)];
  return (
    <MenuSelectItem
      id="revision-source-dropdown"
      size="medium"
      options={options}
      value={options.find((option) => option.value === source) || allSourcesOption}
      onChange={(option) => onClick((option?.value as RevisionSource) || null)}
    />
  );
};

export default SourceDropdown;
